import { useEffect, useState, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment";
import "../Dashboard/Dashboard.css";
import "./AdManagementList.css";
import { toast } from "react-toastify";
import { useLoading } from "../../hooks/useLoader";
import { usePageMode } from "../../hooks/usePageMode";
import {
  GetAllSelectedIds,
  ClearSelectionData,
  SelectionFilter,
} from "../../utilities/SelectionFilter";
import DataTable from "../Tables/DataTable/DataTable";
import TableColumnModal from "../../common/TableColumnModal/TableColumnModal";
import { AdManagementColumns } from "../../common/TableColumnModal/DefaultDataColumns";
import Confirmation from "../../common/Confirmation/Confirmation";
import AddEditAdManagement from "./AddEditAdManagement";
import { MODE } from "../../constants/common";
import { SystemUsersEnum } from "../../constants/SystemUsers";
import { useAuth } from "../../hooks/useAuth";
import PropTypes from "prop-types";
import ApiServices from "../../services/ApiServices";
import {
  AD_LIST,
  AD_CHANGE_STATUS,
  AD_COPY,
  AD_DELETE,
} from "../../constants/api-constants";
import { PAYMENT_STATUS_TEXT } from "./components/Utils/adUtils";

/*istanbul ignore next */
const AdManagementList = (props) => {
  const { showLoader } = useLoading();
  const page = usePageMode();
  const location = useLocation();
  const auth = useAuth();
  /* Super Admin viewing a distributor's Ad Management tab gets the
   * direct-publish flow (no Stripe, no cart). Detected from the
   * currently authenticated user's role. */
  const isSuperAdmin = auth?.getRole?.() === SystemUsersEnum.SUPERADMIN;
  /* Always reflects the current tab URL — used as the stable breadcrumb base. */
  const baseRouteRef = useRef(location.pathname.split("/").filter(Boolean));
  useEffect(() => {
    baseRouteRef.current = location.pathname.split("/").filter(Boolean);
  }, [location.pathname]);

  /* ── Mode management (list / add / edit / view) ──────────────────────── */
  const [currentMode, setCurrentMode] = useState(MODE.LIST);
  const [selectedAdId, setSelectedAdId] = useState(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const adSaveRef = useRef(null);
  /* Tracks whether the Add/Edit form has unsaved changes */
  const adDirtyRef = useRef(false);
  /* Holds the continuation to run after the user resolves the leave popup
     (e.g. perform a tab switch initiated from outside the form). */
  const pendingProceedRef = useRef(null);

  const [adminTable, setAdminTableData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(0);
  const query = useMemo(() => {
    return props.query;
  }, [props.query]);
  const { showColumnPopup, triggerAdd, onTriggerAddHandled } = props;
  const [tableColumnModalVisibility, setTableColumnModalVisibility] =
    useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [adBanner, setAdBanner] = useState(null);
  const [action, setAction] = useState(null);
  const [confirmation, setConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");

  /*istanbul ignore next */
  const handleConfirmationOpen = (actionName, cnfMessage) => {
    setAction(actionName);
    setConfirmationMessage(cnfMessage);
    setConfirmation(true);
  };

  /*istanbul ignore next */
  const handleConfirmationClose = () => {
    setConfirmationMessage("");
    setConfirmation(false);
  };

  useEffect(() => {
    ClearSelectionData();
  }, []);

  /* Register / unregister leave handler (breadcrumb + tab switch) when mode
     changes. The handler accepts an optional `proceed` continuation that
     callers (e.g. tab change) use to resume their navigation after the user
     resolves the unsaved-changes prompt. */
  /* Builds the dirty-navigation handler invoked when the user tries to
     leave (tab switch / breadcrumb). Hoisted out of the effect to keep
     nesting depth shallow. */
  const buildDirtyNavHandler = () => (proceed) => {
    const resume = () => {
      if (typeof proceed === "function") proceed();
    };
    if (adDirtyRef.current) {
      pendingProceedRef.current = () => {
        handleFormCancel();
        resume();
      };
      setShowLeaveConfirm(true);
    } else {
      handleFormCancel();
      resume();
    }
  };

  useEffect(() => {
    if (currentMode !== MODE.LIST) {
      page.setOnDirtyNavigation(buildDirtyNavHandler);
    } else {
      page.setOnDirtyNavigation(null);
    }
    return () => {
      page.setOnDirtyNavigation(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMode]);

  /* Notify parent (DistributorProfile) whenever the form open/close state
   * changes so it can hide BasicInfo header icons while the form is open. */
  useEffect(() => {
    props.onModeChange?.(currentMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMode]);

  useEffect(() => {
    setTableColumnModalVisibility(showColumnPopup);
  }, [showColumnPopup]);

  /* Respond to external "Add" trigger from BasicInfo header button */
  useEffect(() => {
    if (triggerAdd) {
      setSelectedAdId(null);
      setCurrentMode(MODE.ADD);
      onTriggerAddHandled?.();
      page.setBreadcrumbRoute([...baseRouteRef.current, "add-ad"]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerAdd, onTriggerAddHandled]);

  useEffect(() => {
    getAdManagementList();
    setSelectionColumns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const setSelectionColumns = () => {
    let selectedCols = localStorage.getItem("selectedColumns")
      ? JSON.parse(localStorage.getItem("selectedColumns"))
      : null;
    const cached = selectedCols?.adManagementColumns;
    /* Always derive `sort` from the latest defaults so toggling sortability
       in code takes effect for users with cached column preferences. */
    const cols = cached
      ? cached.map((c) => {
          const def = AdManagementColumns.find(
            (d) => d.fieldName === c.fieldName,
          );
          return def ? { ...c, sort: def.sort } : c;
        })
      : AdManagementColumns;
    setSelectedColumns(cols);
  };

  const getAdManagementList = async () => {
    try {
      showLoader(true);
      const payload = {
        pageNumber: query.PageNumber || 1,
        pageSize: query.PageSize || 10,
        ignorePaging: false,
        sortColumn: query.SortColumn || "",
        sortType: query.SortType || "asc",
        filter: {
          distributorId: Number(props.id),
          statusFilter: query.filter?.status ?? [],
          paymentStatusFilter: query.filter?.payment ?? [],
          bannerPositions: query.filter?.position ?? [],
          startDateFrom: query.filter?.startDateFrom ?? null,
          startDateTo: query.filter?.startDateTo ?? null,
          searchText: query.searchText || "",
          selectedIds: [],
        },
        isSearchInAllQuery: true,
        searchText: query?.searchText || "",
      };
      const response = await ApiServices.httpPost(AD_LIST, payload);
      showLoader(false);
      if (response?.responseStatus?.isSuccess) {
        let records = response.result?.records || [];

        // Normalize API field names to match column fieldNames and compute row state
        const POSITION_LABELS = { 1: "Top", 2: "Featured" };
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        records = records.map((r) => {
          const startFormatted = r.startDate
            ? moment(r.startDate).format("MM/DD/YYYY")
            : "";
          const endFormatted = r.endDate
            ? moment(r.endDate).format("MM/DD/YYYY")
            : "";
          const dateRange =
            startFormatted && endFormatted
              ? `${startFormatted} - ${endFormatted}`
              : startFormatted || endFormatted || "";
          const endDateObj = r.endDate ? new Date(r.endDate) : null;
          const startDateObj = r.startDate ? new Date(r.startDate) : null;
          const isExpired = endDateObj ? endDateObj < today : false;
          const isUpcoming =
            !isExpired && startDateObj ? startDateObj > today : false;
          const payment = r?.paymentStatusText ?? "";
          const isDraft = payment === PAYMENT_STATUS_TEXT.DRAFT;
          const isPendingPayment =
            payment === PAYMENT_STATUS_TEXT.PENDING_PAYMENT;
          const isPurchased = payment === PAYMENT_STATUS_TEXT.PURCHASED;
          return {
            ...r,
            adManagementId: r.adId,
            bannerName: r.title ?? "",
            position:
              POSITION_LABELS[r.bannerPosition] ?? r.bannerPositionText ?? "",
            dateRange,
            payment,
            /* Status is Active only when the API says active AND the ad has
             * been fully purchased. Ads still in cart (Draft / Pending
             * Payment) must render as Inactive regardless of adStatus. */
            status: r.adStatus === 1 && isPurchased,
            disableToggleButton: isDraft || isExpired || isPendingPayment,
            isExpired,
            isUpcoming,
          };
        });

        const checkedIds = GetAllSelectedIds("adManagementId");
        if (checkedIds) {
          records = records.map((ele) => ({
            ...ele,
            isChecked: checkedIds.includes(ele.adManagementId),
          }));
        }

        setAdminTableData(records);
        setTotalPages(response.result?.totalPages || 0);
        setPageSize(response.result?.pageSize || query.PageSize || 10);
      } else {
        toast.error(response?.responseStatus?.error || "Internal server error");
      }
    } catch (e) {
      showLoader(false);
      toast.error("Internal server error");
    }
  };

  const onSuccessToggle = () => {
    getAdManagementList();
  };

  const handleChangePageNumber = (event, newPage) => {
    props.setQuery((prevState) => ({
      ...prevState,
      PageNumber: newPage,
    }));
  };

  const pageSizeChangeHandler = (PageSize) => {
    props.setQuery((prevState) => ({
      ...prevState,
      PageSize,
      PageNumber: 1,
    }));
  };

  const onSearchHandler = (event) => {
    ClearSelectionData();
    setTimeout(() => {
      props.setQuery((prevState) => ({
        ...prevState,
        PageNumber: 1,
        searchText: event.target.value,
      }));
    }, 1000);
  };

  const SORT_COLUMN_MAP = {
    bannerName: "title",
    position: "bannerPosition",
    dateRange: "startDate",
    payment: "paymentStatus",
    status: "adStatus",
  };

  const sortHandler = (sortOrder, sortBy) => {
    const apiSortColumn = SORT_COLUMN_MAP[sortBy] || sortBy;
    const nextSortType = sortOrder === "asc" ? "desc" : "asc";
    props.setQuery((prevState) => ({
      ...prevState,
      SortColumn: apiSortColumn,
      SortType: nextSortType,
    }));
  };

  const onCancelTableColumnSelection = () => {
    setTableColumnModalVisibility(false);
    setSelectionColumns();
    props.onCancelTableColumnSelection(false);
  };

  const onSaveTableColumnSelection = (componentName, columns) => {
    let oldData = localStorage.getItem("selectedColumns")
      ? JSON.parse(localStorage.getItem("selectedColumns"))
      : null;
    if (oldData) {
      localStorage.setItem(
        "selectedColumns",
        JSON.stringify({ ...oldData, [componentName]: columns }),
      );
    } else {
      localStorage.setItem(
        "selectedColumns",
        JSON.stringify({ [componentName]: columns }),
      );
    }
    setSelectionColumns();
    setTableColumnModalVisibility(false);
    props.onCancelTableColumnSelection(false);
  };

  const onCheckboxChangeHandler = (e, value) => {
    const { checked } = e.target;
    if (value === "all") {
      setAdminTableData((items) => {
        SelectionFilter(items, "multiple", checked, "adManagementId");
        return items.map((data) => ({ ...data, isChecked: checked }));
      });
    } else {
      setAdminTableData((items) =>
        items.map((row) => {
          let record = { ...row };
          if (row.adManagementId === value) {
            SelectionFilter(row, "single", checked, "adManagementId");
            record.isChecked = checked;
          }
          return record;
        }),
      );
    }
  };

  /*istanbul ignore next */
  const onToggleHandler = async (
    column,
    userId,
    contentSubscriberStatus,
    e,
    record,
  ) => {
    if (record.isExpired) return; /* Phase 3: expired toggle must not fire */
    /* Pending Payment / Draft: toggle disabled — do not open Pause popup. */
    if (record.disableToggleButton) return;
    setAdBanner(record);
    if (record.status === true) {
      handleConfirmationOpen(
        "Inactivate",
        "Are you sure you want to mark this ad as Inactive?",
      );
    } else {
      changeAdStatus(record, "", 1);
    }
  };

  /*istanbul ignore next */
  const changeAdStatus = async (adObj, reason, status) => {
    try {
      showLoader(true);
      const payload = {
        adId: adObj.adId ?? adObj.adManagementId,
        adStatus: status,
      };
      const response = await ApiServices.httpPut(AD_CHANGE_STATUS, payload);
      if (response?.responseStatus?.isSuccess) {
        toast.success(
          response.responseStatus.description || "Status updated successfully.",
        );
        getAdManagementList();
      } else {
        toast.error(
          response?.responseStatus?.error || "Failed to update status.",
        );
      }
    } catch (event) {
      toast.error("Internal server error");
    } finally {
      showLoader(false);
    }
  };

  /*istanbul ignore next */
  const deleteAd = async (adObj) => {
    try {
      showLoader(true);
      const adId = adObj.adId ?? adObj.adManagementId;
      const response = await ApiServices.httpDelete(AD_DELETE(adId));
      if (response?.responseStatus?.isSuccess) {
        toast.success(
          response.responseStatus.description || "Ad deleted successfully.",
        );
        getAdManagementList();
      } else {
        toast.error(response?.responseStatus?.error || "Failed to delete ad.");
      }
    } catch (event) {
      toast.error("Internal server error");
    } finally {
      showLoader(false);
    }
  };

  /*istanbul ignore next */
  const copyAd = async (adObj) => {
    try {
      showLoader(true);
      const adId = adObj.adId ?? adObj.adManagementId;
      const response = await ApiServices.httpPost(AD_COPY(adId), {});
      if (response?.responseStatus?.isSuccess) {
        toast.success(
          response.responseStatus.description || "Ad copied successfully.",
        );
        const newAdId = response.result;
        if (newAdId) {
          /* Open the newly-copied ad directly in Edit mode so the user can
           * tweak it before publishing. */
          setSelectedAdId(newAdId);
          setCurrentMode(MODE.EDIT);
          page.setBreadcrumbRoute([...baseRouteRef.current, "edit-ad"]);
        } else {
          getAdManagementList();
        }
      } else {
        toast.error(response?.responseStatus?.error || "Failed to copy ad.");
      }
    } catch (event) {
      toast.error("Internal server error");
    } finally {
      showLoader(false);
    }
  };

  /*istanbul ignore next */
  const actionHandler = (actionLabel, record) => {
    if (actionLabel === "Delete") {
      setAdBanner(record);
      const isPurchased = record.payment === "Purchased";
      const message = isPurchased
        ? "This ad has been paid for. Deleting it will remove it permanently and no refund will be issued. Are you sure?"
        : "Are you sure you want to delete this ad? This action cannot be undone.";
      handleConfirmationOpen("Delete", message);
    } else if (actionLabel === "Copy") {
      copyAd(record);
    }
  };

  /*istanbul ignore next */
  const handleEditClick = (record) => {
    setSelectedAdId(record.adManagementId);
    setCurrentMode(MODE.EDIT);
    page.setBreadcrumbRoute([...baseRouteRef.current, "edit-ad"]);
  };

  /*istanbul ignore next */
  const handleViewClick = (record) => {
    setSelectedAdId(record.adManagementId);
    setCurrentMode(MODE.VIEW);
  };

  /*istanbul ignore next */
  const handleFormCancel = () => {
    setCurrentMode(MODE.LIST);
    setSelectedAdId(null);
    page.setBreadcrumbRoute(baseRouteRef.current);
  };

  /* Cancel requested from the form — guard with unsaved-changes prompt */
  /*istanbul ignore next */
  const requestFormCancel = () => {
    if (adDirtyRef.current) {
      setShowLeaveConfirm(true);
    } else {
      handleFormCancel();
    }
  };

  /*istanbul ignore next */
  const handleFormSave = () => {
    setCurrentMode(MODE.LIST);
    setSelectedAdId(null);
    getAdManagementList();
    page.setBreadcrumbRoute(baseRouteRef.current);
  };

  return (
    <div className="dashboard-main">
      <Confirmation
        open={showLeaveConfirm}
        width={480}
        title="Unsaved Changes"
        description="You have unsaved changes. Save before leaving or discard your changes?"
        confirmButtonLabel="Save & Exit"
        cancelButtonLabel="Discard Changes"
        handleClose={() => {
          setShowLeaveConfirm(false);
          pendingProceedRef.current = null;
        }}
        onCancel={() => {
          setShowLeaveConfirm(false);
          const proceed = pendingProceedRef.current;
          pendingProceedRef.current = null;
          if (proceed) proceed();
          else handleFormCancel();
        }}
        onConfirm={() => {
          setShowLeaveConfirm(false);
          /* Save-and-exit: trigger save; pending tab switch (if any) is
             discarded — user lands on the ad list after a successful save. */
          pendingProceedRef.current = null;
          adSaveRef.current?.();
        }}
      />
      {currentMode !== MODE.LIST ? (
        /* ── Add / Edit / View form ─────────────────────────────────────── */
        <AddEditAdManagement
          mode={currentMode}
          adId={selectedAdId}
          distributorId={props.id}
          isSuperAdmin={isSuperAdmin}
          onCancel={requestFormCancel}
          onSave={handleFormSave}
          onEdit={() => {
            setCurrentMode(MODE.EDIT);
            page.setBreadcrumbRoute([...baseRouteRef.current, "edit-ad"]);
          }}
          saveRef={adSaveRef}
          dirtyRef={adDirtyRef}
          permissions={props?.permissions}
        />
      ) : (
        /* ── List view ──────────────────────────────────────────────────── */
        <>
          <DataTable
            uniqueKey="adManagementId"
            onCheckboxChange={onCheckboxChangeHandler}
            columns={selectedColumns}
            query={query}
            handleChangePage={handleChangePageNumber}
            success={onSuccessToggle}
            adminTable={adminTable}
            onEditSuccess={() => {}}
            totalPages={totalPages}
            pageSize={pageSize}
            onSearch={onSearchHandler}
            onSort={sortHandler}
            onPageSizeChange={pageSizeChangeHandler}
            showView={true}
            showEdit={props.permissions?.includes("Update")}
            onView={handleViewClick}
            onEdit={handleEditClick}
            onToggle={onToggleHandler}
            showToggle={true}
            showMenu={true}
            actionHandler={actionHandler}
            filterMenuItems={(options, row) => {
              const isPurchasedActive =
                row.payment === "Purchased" && row.status === true;
              return options.filter(
                (opt) => !(opt.label === "Delete" && isPurchasedActive),
              );
            }}
            permissions={props.permissions}
          />
          {tableColumnModalVisibility && (
            <TableColumnModal
              isOpen={tableColumnModalVisibility}
              onCancelTableColumnSelection={onCancelTableColumnSelection}
              onSaveTableColumnSelection={onSaveTableColumnSelection}
              selection={selectedColumns}
            />
          )}
          <Confirmation
            open={confirmation}
            handleClose={handleConfirmationClose}
            title={
              action === "Inactivate" ? "Pause This Ad?" : confirmationMessage
            }
            description={
              action === "Inactivate" ? (
                <div
                  style={{
                    backgroundColor: "#FFF8E1",
                    border: "1px solid #FFE0B2",
                    borderRadius: 6,
                    padding: "12px 16px",
                    color: "#333",
                    fontSize: 14,
                    lineHeight: 1.5,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: "#B26A00",
                      marginBottom: 6,
                    }}
                  >
                    Pausing this ad will stop it from displaying on a
                    subscriber&apos;s homepage.
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    Your campaign dates will continue to run and no refund will
                    be issued.
                  </div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    You may still:
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    <li>Reactivate this ad within your start and end dates</li>
                    <li>Replace the ad file (upload a new banner image)</li>
                    <li>Change the banner name</li>
                  </ul>
                </div>
              ) : (
                ""
              )
            }
            cancelButtonLabel="Cancel"
            confirmButtonLabel={
              action === "Inactivate" ? "Confirm Pause" : "Yes"
            }
            showReason={false}
            onConfirm={async (e, reason) => {
              if (action === "Activate" || action === "Inactivate") {
                changeAdStatus(adBanner, reason, action === "Activate" ? 1 : 0);
              } else if (action === "Delete") {
                deleteAd(adBanner);
              }
              handleConfirmationClose();
            }}
            onCancel={() => handleConfirmationClose()}
          />
        </>
      )}
    </div>
  );
};

AdManagementList.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  query: PropTypes.object,
  showColumnPopup: PropTypes.bool,
  permissions: PropTypes.arrayOf(PropTypes.string),
  setQuery: PropTypes.func,
  onCancelTableColumnSelection: PropTypes.func,
  triggerAdd: PropTypes.bool,
  onTriggerAddHandled: PropTypes.func,
  onModeChange: PropTypes.func,
};

AdManagementList.defaultProps = {
  query: {},
  showColumnPopup: false,
  permissions: [],
  setQuery: () => {},
  onCancelTableColumnSelection: () => {},
  triggerAdd: false,
  onTriggerAddHandled: () => {},
  onModeChange: () => {},
};

export default AdManagementList;
